import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import producerProfilesApi from 'api/requests/producerProfiles';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import Text from 'components/_UI/Text';
import Loader from 'components/_UI/Loader';
import EventCard from 'modules/events/card/EventCard';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import { FullyCentered } from 'styled/alignment/Center';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import paths from 'routes/paths';
import { Heart, UserCheck, Calendar, Clock } from 'lucide-react';
import Footer from 'components/commons/Footer';
import { useAuth } from 'context/auth/Auth';
import LoginModal from 'modules/onboarding/sign-in/LoginModal';
import useFollow from 'hooks/useFollow';
import { buildSocialLinks } from 'theme/producerSocialIcons';

export default function ProducerPublicPage() {
    const { slug } = useParams();
    const { isAuth, user } = useAuth();
    const [producer, setProducer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeTab, setActiveTab] = useState('upcoming');
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [pastEvents, setPastEvents] = useState([]);
    const [upcomingPage, setUpcomingPage] = useState(1);
    const [pastPage, setPastPage] = useState(1);
    const [upcomingLastPage, setUpcomingLastPage] = useState(1);
    const [pastLastPage, setPastLastPage] = useState(1);
    const [upcomingTotal, setUpcomingTotal] = useState(0);
    const [pastTotal, setPastTotal] = useState(0);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const { isFollowing, toggleFollow } = useFollow(producer?.user_id, producer?.is_following);
    const isOwner = isAuth && user?.id === producer?.user_id;

    const socials = buildSocialLinks(producer?.socialMedia);

    useEffect(() => {
        if (slug) {
            getProducerData();
        }
    }, [slug]);

    useEffect(() => {
        if (slug && producer) {
            fetchEvents('active', 1, true);
            fetchEvents('expired', 1, true);
        }
    }, [slug, producer]);

    async function getProducerData() {
        setLoading(true);
        setError(false);
        try {
            const { data } = await producerProfilesApi.getBySlug(slug);
            setProducer(data?.data);
        } catch (err) {
            setError(true);
            setProducer(null);
        } finally {
            setLoading(false);
        }
    }

    const fetchEvents = useCallback(async (status, page = 1, reset = false) => {
        setLoadingEvents(true);
        try {
            const { data } = await producerProfilesApi.getEvents(slug, status, page);
            const events = data?.data || [];
            const lastPage = data?.meta?.last_page || data?.last_page || 1;
            const total = data?.meta?.total ?? events.length;

            if (status === 'active') {
                setUpcomingEvents(prev => reset ? events : [...prev, ...events]);
                setUpcomingPage(page);
                setUpcomingLastPage(lastPage);
                setUpcomingTotal(total);
            } else {
                setPastEvents(prev => reset ? events : [...prev, ...events]);
                setPastPage(page);
                setPastLastPage(lastPage);
                setPastTotal(total);
            }
        } catch (err) {
            // silently fail for events fetch
        } finally {
            setLoadingEvents(false);
        }
    }, [slug]);

    function handleFollow() {
        toggleFollow(() => setIsLoginModalOpen(true));
    }

    function onEventClick(event) {
        window.open(`${paths.events.page}/${event?.slug}`, '_blank');
    }

    function loadMoreUpcoming() {
        if (upcomingPage < upcomingLastPage) {
            fetchEvents('active', upcomingPage + 1);
        }
    }

    function loadMorePast() {
        if (pastPage < pastLastPage) {
            fetchEvents('expired', pastPage + 1);
        }
    }

    if (loading) {
        return (
            <PageContainer>
                <FullyCentered style={{ minHeight: '60vh' }}>
                    <Loader />
                </FullyCentered>
            </PageContainer>
        );
    }

    if (error || !producer) {
        return (
            <PageContainer>
                <FullyCentered style={{ minHeight: '60vh' }}>
                    <Column horizontalCenter>
                        <Text name='subtitle' color={colors.nightDark}>Produtor não encontrado</Text>
                        <Text color={colors.night}>O perfil que você procura não existe ou foi removido.</Text>
                    </Column>
                </FullyCentered>
            </PageContainer>
        );
    }

    const totalEvents = upcomingTotal + pastTotal;

    return (
        <PageContainer>
            <ProfileHeader>
                <Banner />

                <ProfileHeaderInner>
                    <AvatarWrapper>
                        <DynamicProfileImage
                            img={producer?.photoImage?.path}
                            size={'136px'}
                            word={producer?.name}
                        />
                    </AvatarWrapper>

                    <HeaderContent>
                        <HeaderTopRow>
                            <NameGroup>
                                <ProducerName>{producer?.name}</ProducerName>
                                <StatsRow>
                                    <StatItem>
                                        <Calendar size={14} />
                                        {totalEvents} evento{totalEvents !== 1 ? 's' : ''}
                                    </StatItem>
                                </StatsRow>
                            </NameGroup>

                            <ActionButtons>
                                {!isOwner && (
                                    <FollowButton onClick={handleFollow} $following={isFollowing}>
                                        {isFollowing ? <UserCheck size={16} /> : <Heart size={16} />}
                                        {isFollowing ? 'Seguindo' : 'Seguir'}
                                    </FollowButton>
                                )}
                            </ActionButtons>
                        </HeaderTopRow>

                        {producer?.description && (
                            <ProducerDescription>{producer?.description}</ProducerDescription>
                        )}

                        {socials.length > 0 && (
                            <SocialLinksRow>
                                {socials.map(social => (
                                    <SocialLinkButton
                                        key={social.key}
                                        href={social.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        aria-label={social.label}
                                        title={social.label}
                                    >
                                        <social.Icon size={18} />
                                        <SocialLinkLabel>{social.label}</SocialLinkLabel>
                                    </SocialLinkButton>
                                ))}
                            </SocialLinksRow>
                        )}
                    </HeaderContent>
                </ProfileHeaderInner>

                <TabBar>
                    <TabBarInner>
                        <Tab
                            $active={activeTab === 'upcoming'}
                            onClick={() => setActiveTab('upcoming')}
                        >
                            Disponíveis
                        </Tab>
                        <Tab
                            $active={activeTab === 'past'}
                            onClick={() => setActiveTab('past')}
                        >
                            Encerrados
                        </Tab>
                    </TabBarInner>
                </TabBar>
            </ProfileHeader>

            <ContentSection>
                <ContentInner>
                    {activeTab === 'upcoming' && (
                        <>
                            {upcomingEvents.length > 0 ? (
                                <>
                                    <EventsGrid>
                                        {upcomingEvents.map(event => (
                                            <EventCard
                                                key={event.id || event.slug}
                                                data={event}
                                                fullWidth
                                                whiteBackground
                                                onEventClick={onEventClick}
                                            />
                                        ))}
                                    </EventsGrid>
                                    {upcomingPage < upcomingLastPage && (
                                        <LoadMoreWrapper>
                                            <LoadMoreButton onClick={loadMoreUpcoming} disabled={loadingEvents}>
                                                {loadingEvents ? 'Carregando...' : 'Ver mais eventos'}
                                            </LoadMoreButton>
                                        </LoadMoreWrapper>
                                    )}
                                </>
                            ) : (
                                <EmptyState>
                                    {loadingEvents ? (
                                        <Loader />
                                    ) : (
                                        <>
                                            <Calendar size={40} color={colors.nightLight} />
                                            <Text color={colors.night} marginTop='12px' textAlign='center'>
                                                Nenhum evento disponível no momento.
                                            </Text>
                                        </>
                                    )}
                                </EmptyState>
                            )}
                        </>
                    )}

                    {activeTab === 'past' && (
                        <>
                            {pastEvents.length > 0 ? (
                                <>
                                    <EventsGrid>
                                        {pastEvents.map(event => (
                                            <EventCard
                                                key={event.id || event.slug}
                                                data={event}
                                                fullWidth
                                                whiteBackground
                                                onEventClick={onEventClick}
                                                expired
                                            />
                                        ))}
                                    </EventsGrid>
                                    {pastPage < pastLastPage && (
                                        <LoadMoreWrapper>
                                            <LoadMoreButton onClick={loadMorePast} disabled={loadingEvents}>
                                                {loadingEvents ? 'Carregando...' : 'Ver mais eventos'}
                                            </LoadMoreButton>
                                        </LoadMoreWrapper>
                                    )}
                                </>
                            ) : (
                                <EmptyState>
                                    {loadingEvents ? (
                                        <Loader />
                                    ) : (
                                        <>
                                            <Clock size={40} color={colors.nightLight} />
                                            <Text color={colors.night} marginTop='12px' textAlign='center'>
                                                Nenhum evento encerrado.
                                            </Text>
                                        </>
                                    )}
                                </EmptyState>
                            )}
                        </>
                    )}
                </ContentInner>
            </ContentSection>

            <Footer />

            <LoginModal
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
            />
        </PageContainer>
    );
}

const PageContainer = styled.div`
    width: 100%;
    min-height: 100vh;
    background: ${colors.neutralBackground};

    @media screen and (min-width: ${breakpoints.lg}) {
        padding-top: 95px;
    }
`;

const ProfileHeader = styled.div`
    width: 100%;
    position: relative;
    background: ${colors.neutralLight};
    border-bottom: 1px solid ${colors.neutralDark};
`;

const Banner = styled.div`
    width: 100%;
    height: 96px;
    background: linear-gradient(120deg, ${colors.occasionalPurple}, ${colors.primaryLight});

    @media screen and (min-width: ${breakpoints.md}) {
        height: 140px;
    }
`;

const ProfileHeaderInner = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    position: relative;
    padding: 0 24px 24px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 0 24px 28px;
    }
`;

const AvatarWrapper = styled.div`
    position: absolute;
    top: -68px;
    left: 50%;
    transform: translateX(-50%);
    width: 136px;
    height: 136px;
    border-radius: 50%;
    overflow: hidden;
    border: 5px solid ${colors.neutralLight};
    background: ${colors.neutralLight};
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);

    & > picture > img {
        object-fit: cover;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        left: 24px;
        transform: none;
    }
`;

const HeaderContent = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    text-align: center;
    padding-top: 84px;

    @media screen and (min-width: ${breakpoints.md}) {
        align-items: flex-start;
        text-align: left;
        padding-top: 16px;
        padding-bottom: 6px;
        margin-left: 160px;
    }
`;

const HeaderTopRow = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: flex-end;
        justify-content: space-between;
    }
`;

const NameGroup = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;

    @media screen and (min-width: ${breakpoints.md}) {
        align-items: flex-start;
    }
`;

const ProducerName = styled.h1`
    font-size: ${fonts.sizes.title};
    font-weight: ${fonts.weight.bold};
    color: ${colors.nightDark};
    margin: 0;
    text-align: center;

    @media screen and (min-width: ${breakpoints.md}) {
        font-size: 28px;
        text-align: left;
    }
`;

const StatsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const StatItem = styled.span`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    font-weight: ${fonts.weight.bold};
`;

const ProducerDescription = styled.p`
    width: 100%;
    max-width: 640px;
    font-size: ${fonts.sizes.default};
    color: ${colors.night};
    margin: 0;
    text-align: center;
    line-height: 1.5;

    @media screen and (min-width: ${breakpoints.md}) {
        text-align: left;
    }
`;

const SocialLinksRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 8px;

    @media screen and (min-width: ${breakpoints.md}) {
        justify-content: flex-start;
    }
`;

const SocialLinkButton = styled.a`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px 7px 9px;
    border-radius: 20px;
    background: ${colors.neutralBackground};
    color: ${colors.nightDark};
    border: 1px solid ${colors.neutralDark};
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.occasionalPurple}15;
        border-color: ${colors.occasionalPurple};
        color: ${colors.occasionalPurple};
    }
`;

const SocialLinkLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: ${fonts.weight.bold};

    @media screen and (max-width: ${breakpoints.sm}) {
        display: none;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
`;

const FollowButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    border-radius: 6px;
    font-size: ${fonts.sizes.default};
    font-weight: ${fonts.weight.bold};
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    border: 2px solid ${({ $following }) => $following ? colors.neutralDark : colors.occasionalPurple};
    background: ${({ $following }) => $following ? colors.neutralLight : colors.occasionalPurple};
    color: ${({ $following }) => $following ? colors.night : colors.neutralLight};

    &:hover {
        border-color: ${colors.occasionalPurple};
        background: ${({ $following }) => $following ? colors.neutralDark : colors.secondaryPurple};
    }
`;

const TabBar = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 24px;
`;

const TabBarInner = styled.div`
    display: flex;
    gap: 32px;
    border-bottom: none;
    justify-content: center;

    @media screen and (min-width: ${breakpoints.md}) {
        justify-content: flex-start;
        padding-left: 160px;
    }
`;

const Tab = styled.button`
    padding: 16px 0;
    border: none;
    background: none;
    font-size: ${fonts.sizes.default};
    font-weight: ${fonts.weight.bold};
    cursor: pointer;
    transition: all 0.2s ease;
    color: ${({ $active }) => $active ? colors.nightDark : colors.night};
    border-bottom: 3px solid ${({ $active }) => $active ? colors.occasionalPurple : 'transparent'};
    margin-bottom: -1px;

    &:hover {
        color: ${colors.nightDark};
    }
`;

const ContentSection = styled.div`
    width: 100%;
    background: ${colors.neutralBackground};
    min-height: 300px;
`;

const ContentInner = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 32px 24px 48px;
`;

const EventsGrid = styled(Column)`
    width: 100%;
    gap: 4px;

    @media screen and (min-width: ${breakpoints.sm}) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
    }
`;

const EmptyState = styled(Column)`
    align-items: center;
    justify-content: center;
    padding: 64px 20px;
`;

const LoadMoreWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 32px;
`;

const LoadMoreButton = styled.button`
    padding: 12px 32px;
    background: ${colors.neutralLight};
    color: ${colors.occasionalPurple};
    border: 2px solid ${colors.occasionalPurple};
    border-radius: 6px;
    font-size: ${fonts.sizes.default};
    font-weight: ${fonts.weight.bold};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${colors.occasionalPurple};
        color: ${colors.neutralLight};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;
